/**
 * Retailer Configuration
 * 
 * Brand colors, logos, and metadata for all retail partners.
 * Colors are subtle variants that complement PatternIQ's blue/purple theme.
 */

// Retailer UUIDs (from database)
export const RETAILER_IDS = {
  ULTA: '10000000-0000-0000-0000-000000000001',
  SEPHORA: '10000000-0000-0000-0000-000000000002',
  MACYS: '10000000-0000-0000-0000-000000000003',
  JCP: '10000000-0000-0000-0000-000000000004',
} as const;

// DTC Platform Colors (for ecommerce channels)
export const DTC_PLATFORM_COLORS = {
  walmart: {
    primary: '#0071CE',      // Walmart blue
    light: '#F0F6FF',        // Very light blue background
    medium: '#B4D4F1',       // Medium blue for subtle accents
    dark: '#004F9A',         // Dark blue for text/borders
    chartColor: '#0071CE',   // Walmart blue for charts
  },
  shopify: {
    primary: '#96BF48',      // Shopify green
    light: '#F6FFF0',        // Very light green background
    medium: '#D4F1B4',       // Light green for subtle accents
    dark: '#6B8F35',         // Dark green for text/borders
    chartColor: '#96BF48',   // Shopify green for charts
  },
} as const;

// Retailer brand colors (subtle, PatternIQ-compatible)
export const RETAILER_COLORS = {
  ulta: {
    primary: '#FF6B35',      // Warm orange (Ulta's signature color)
    light: '#FFF4F0',        // Very light peachy background
    medium: '#FFD4C4',       // Light orange for subtle accents
    dark: '#E85A24',         // Darker orange for text/borders
    chartColor: '#FF8C5F',   // Brighter for charts
  },
  jcp: {
    primary: '#000000',      // JCP black (modern rebrand)
    light: '#F5F5F5',        // Very light gray background
    medium: '#CCCCCC',       // Light gray for subtle accents
    dark: '#1A1A1A',         // Soft black for text/borders
    chartColor: '#333333',   // Dark gray for charts
  },
  sephora: {
    primary: '#000000',      // Sephora black
    light: '#F8F8F8',        // Light gray background
    medium: '#E0E0E0',       // Medium gray for subtle accents
    dark: '#1A1A1A',         // Soft black for text/borders
    chartColor: '#4A4A4A',   // Gray for charts
  },
  macys: {
    primary: '#E21B23',      // Macy's red star
    light: '#FFF5F6',        // Very light red background
    medium: '#FFD7D9',       // Light red for subtle accents
    dark: '#B51519',         // Darker red for text/borders
    chartColor: '#F03238',   // Brighter for charts
  },
} as const;

// Retailer metadata
export const RETAILER_INFO: Record<string, {
  id: string;
  name: string;
  slug: string;
  colors: typeof RETAILER_COLORS[keyof typeof RETAILER_COLORS];
  logoPath?: string;
  type: 'brick_mortar' | 'ecommerce' | 'hybrid';
  dateFormat?: 'fiscal_week' | 'period_week' | 'calendar';
  usesCalendarDates?: boolean;
  weeklyMetricsPriority?: boolean;
}> = {
  [RETAILER_IDS.ULTA]: {
    id: RETAILER_IDS.ULTA,
    name: 'Ulta Beauty',
    slug: 'ulta',
    colors: RETAILER_COLORS.ulta,
    logoPath: '/assets/retailer-logos/ulta.png',
    type: 'brick_mortar',
    dateFormat: 'fiscal_week',
    usesCalendarDates: false,
    weeklyMetricsPriority: true,
  },
  [RETAILER_IDS.JCP]: {
    id: RETAILER_IDS.JCP,
    name: 'JCPenney',
    slug: 'jcp',
    colors: RETAILER_COLORS.jcp,
    logoPath: '/assets/retailer-logos/jcpenney.png',
    type: 'brick_mortar',
    dateFormat: 'fiscal_week',
    usesCalendarDates: false,
    weeklyMetricsPriority: true,
  },
  [RETAILER_IDS.SEPHORA]: {
    id: RETAILER_IDS.SEPHORA,
    name: 'Sephora',
    slug: 'sephora',
    colors: RETAILER_COLORS.sephora,
    logoPath: '/assets/retailer-logos/sephora.png',
    type: 'brick_mortar',
  },
  [RETAILER_IDS.MACYS]: {
    id: RETAILER_IDS.MACYS,
    name: "Macy's",
    slug: 'macys',
    colors: RETAILER_COLORS.macys,
    logoPath: '/assets/retailer-logos/macys.png',
    type: 'brick_mortar',
    dateFormat: 'period_week',      // Uses P9W4 format
    usesCalendarDates: true,        // Week-ending dates, not fiscal weeks
    weeklyMetricsPriority: true,    // Weekly (WK) metrics for cross-retailer comparison
  },
};

// Get retailer info by ID
export function getRetailerInfo(retailerId: string) {
  return RETAILER_INFO[retailerId] || null;
}

// Get retailer info by slug
export function getRetailerBySlug(slug: string) {
  return Object.values(RETAILER_INFO).find(r => r.slug === slug) || null;
}

// Get all retailer colors for charts (in order)
export function getAllRetailerChartColors(): string[] {
  return [
    RETAILER_COLORS.ulta.chartColor,
    RETAILER_COLORS.jcp.chartColor,
    RETAILER_COLORS.sephora.chartColor,
    RETAILER_COLORS.macys.chartColor,
  ];
}

// DTC Platform metadata (for ecommerce/marketplace channels)
export const DTC_PLATFORM_INFO: Record<string, {
  name: string;
  slug: string;
  colors: typeof DTC_PLATFORM_COLORS[keyof typeof DTC_PLATFORM_COLORS];
  logoPath?: string;
  type: 'ecommerce' | 'marketplace';
}> = {
  walmart: {
    name: 'Walmart.com',
    slug: 'walmart',
    colors: DTC_PLATFORM_COLORS.walmart,
    logoPath: '/assets/platform-logos/walmart.png',
    type: 'marketplace',
  },
  shopify: {
    name: 'Shopify Store',
    slug: 'shopify',
    colors: DTC_PLATFORM_COLORS.shopify,
    logoPath: '/assets/platform-logos/shopify.png',
    type: 'ecommerce',
  },
};

// Get DTC platform info by slug
export function getDTCPlatformInfo(platformSlug: string) {
  return DTC_PLATFORM_INFO[platformSlug] || null;
}

// Get DTC platform colors by type
export function getDTCPlatformColors(platformType: string): typeof DTC_PLATFORM_COLORS[keyof typeof DTC_PLATFORM_COLORS] | null {
  return DTC_PLATFORM_COLORS[platformType as keyof typeof DTC_PLATFORM_COLORS] || null;
}

